import React, { useEffect, useState } from 'react';
import {
    Button,
    Container,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { API } from '../../utils/Api';
import { toast } from 'react-toastify';
import '../../saas/screens/organizationSettings.scss';

function OrganizationSettings() {
    const token = localStorage.getItem('token');
    const [loading, setLoading] = useState(false);
    const [settingsForm, setSettingsForm] = useState({
        pickup_meters: 0,
        drop_meters: 0,
        api_path: ''
    });

    const validationSchema = Yup.object({
        pickup_meters: Yup.number()
            .typeError("Pickup meters must be a number")
            .required("Pickup meters is required")
            .integer("Pickup meters must be an integer")
            .min(0, "Pickup meters must be positive"),
        drop_meters: Yup.number()
            .typeError("Drop meters must be a number")
            .required("Drop meters is required")
            .integer("Drop meters must be an integer")
            .min(0, "Drop meters must be positive"),
        api_path: Yup.string().url("Invalid URL format").nullable().notRequired()
    });

    const getOrganizationData = async () => {
        if (!token) {
            toast.error("Unauthorized: Please log in again.");
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${API.BASE_URL}client/settings`, {
                headers: { Authorization: `${token}` }
            });

            const data = response.data.data.settings;
            setSettingsForm({
                pickup_meters: Number(data.pickup_meters),
                drop_meters: Number(data.drop_meters),
                api_path: data.order_status_api_path || ''
            });
        } catch (error) {
            toast.error("An error occurred while fetching client's settings");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitOrg = async (values) => {
        if (!token) {
            toast.error("Unauthorized: Please log in again.");
            return;
        }

        const updatedOrgSettings = {
            pickup_meters: values.pickup_meters,
            drop_meters: values.drop_meters,
            ...(values.api_path && { api_path: values.api_path })
        };

        try {
            setLoading(true);
            await axios.post(`${API.BASE_URL}client/settings/update`, updatedOrgSettings, {
                headers: { Authorization: `${token}` }
            });
            toast.success("Settings updated successfully");
            getOrganizationData();
        } catch (error) {
            console.error("Error updating the settings", error);
            toast.error("Failed to update the settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getOrganizationData();
    }, []);

    return (
        <div className='settingsMain'>
            <Formik
                initialValues={settingsForm}
                onSubmit={handleSubmitOrg}
                validationSchema={validationSchema}
                enableReinitialize={true}
            >
                {({ handleChange, handleBlur, handleSubmit, values, errors, touched }) => (
                    <Container maxWidth="xl">
                        <Paper className='settingsSection'>
                            <div className='orgSettingsSection'>
                                <Typography sx={{ fontWeight: '600', fontSize: '18px', color: 'white' }}>
                                    ORGANIZATION SETTINGS
                                </Typography>
                            </div>
                            <Container>
                                <div className='settingsTextSection'>

                                    {/* Pickup Meters */}
                                    <TextField
                                        className='settingsTextField'
                                        label="Pick-Up Meters"
                                        size='small'
                                        type='number'
                                        value={values.pickup_meters}
                                        onChange={handleChange('pickup_meters')}
                                        onBlur={handleBlur('pickup_meters')}
                                        fullWidth
                                        required
                                        inputProps={{ min: 0 }}
                                    />
                                    {touched.pickup_meters && errors.pickup_meters && (
                                        <Typography style={{ color: 'red', fontSize: '10px' }}>
                                            {errors.pickup_meters}
                                        </Typography>
                                    )}

                                    {/* Drop Meters */}
                                    <TextField
                                        className='settingsTextField'
                                        label="Drop Meters"
                                        size='small'
                                        type='number'
                                        value={values.drop_meters}
                                        onChange={handleChange('drop_meters')}
                                        onBlur={handleBlur('drop_meters')}
                                        fullWidth
                                        required
                                        inputProps={{ min: 0 }}
                                    />
                                    {touched.drop_meters && errors.drop_meters && (
                                        <Typography style={{ color: 'red', fontSize: '10px' }}>
                                            {errors.drop_meters}
                                        </Typography>
                                    )}

                                    {/* API Path (Optional) */}
                                    <TextField
                                        className='settingsTextField'
                                        label="API Callback Path (Optional)"
                                        size='small'
                                        type='url'
                                        value={values.api_path}
                                        onChange={handleChange('api_path')}
                                        onBlur={handleBlur('api_path')}
                                        fullWidth
                                    />
                                    {touched.api_path && errors.api_path && (
                                        <Typography style={{ color: 'red', fontSize: '10px' }}>
                                            {errors.api_path}
                                        </Typography>
                                    )}

                                    {/* Submit Button */}
                                    <div className='settingsButtonSection'>
                                        <Button
                                            onClick={handleSubmit}
                                            variant='contained'
                                            disabled={loading}
                                            sx={{ borderRadius: '20px', backgroundColor: '#1E9CED', width: '100%' }}
                                        >
                                            {loading ? 'Updating...' : 'Update Settings'}
                                        </Button>
                                    </div>
                                </div>
                            </Container>
                        </Paper>
                    </Container>
                )}
            </Formik>
        </div>
    );
}

export default OrganizationSettings;
